'use client';
import { useContext, useRef } from 'react';
import { mpObject } from '../interfaces/services/tracking-service.interface';
import { ServicesHandler } from '../services/service-handler';
import GlobalContext from '../context/global-context';
import { UserProfileContext } from '../context/user-profile-context';

// -----------------------------------------------------------------------------
interface UseAnalyticsHook {
  track: (name: string, extra?: mpObject) => void;
  gtagPush: (object: object) => void;
  setUserName: (name: string) => void;
}

// -----------------------------------------------------------------------------
export const useAnalytics = (): UseAnalyticsHook => {
  // The tracking + gtag services are owned by ServicesHandler (the website's DI)
  // rather than instantiated here. config comes from GlobalContext; the tracking
  // services don't use it, and by the time events fire the handler is already
  // built with the real config (UserProfileProvider resolves it first).
  const { config } = useContext(GlobalContext);
  const { owTrackingService, gTagTrackingService } =
    ServicesHandler.getInstance(config);

  // Read the profile context directly (not the throwing useUserProfile) so
  // useAnalytics stays usable even where no provider is above it — notably
  // inside UserProfileProvider, which itself consumes useAnalytics. Refs let the
  // deferred send below read the latest values at fire time (after the await).
  const profile = useContext(UserProfileContext);
  const waitForLoginCheckRef = useRef(profile?.waitForLoginCheck);
  waitForLoginCheckRef.current = profile?.waitForLoginCheck;
  const isPostLoginRef = useRef<boolean>(!!profile?.isPostLogin);
  isPostLoginRef.current = !!profile?.isPostLogin;

  const track = (name: string, extra: mpObject = {}): void => {
    // Defer the send until the login check has resolved, so user attribution and
    // postLogin are final; then stamp postLogin (read after the wait). Where no
    // provider is above us the wait is a no-op and the event sends immediately.
    void (async (): Promise<void> => {
      await waitForLoginCheckRef.current?.();
      owTrackingService?.track(name, {
        ...extra,
        postLogin: isPostLoginRef.current,
      });
    })();
  };

  const gtagPush = (object: object): void => {
    gTagTrackingService?.gtagPush(object);
  };

  const setUserName = (name: string): void => {
    owTrackingService?.setUserName(name);
  };

  return {
    track,
    gtagPush,
    setUserName,
  };
};
